import { withTransaction } from '@elastic/apm-rum-react'
import { Button, FirebaseConnect, Input, JessicaJonesService, LogService, MySquid, Provider } from '@hub/shared'
import useDoLogin from '@src/hooks/use-do-login/use-do-login.hook'
import { AxiosError } from 'axios'
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

const VerifyPageComponent = ({ configs }: { configs: MySquid }) => {
  const logService = useMemo(() => new LogService(), [])
  const jessicaJonesService = useMemo(() => new JessicaJonesService(), [])
  const [loadingButton, setLoadingButton] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [validation, setValidation] = useState({ email: false })
  const [verify, setVerify] = useState({ email: '' })
  const [error, setError] = useState('')

  const { search } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('verifyPage')
  const { doLoginWithProvider, getUtms, saveUtms } = useDoLogin({})

  const verifyEmail = useCallback(
    async (email: string) => {
      const utms = await getUtms()
      return jessicaJonesService
        .verifyEmail(email, undefined, undefined, utms)
        .then((response) => response)
        .catch((error: Error) => {
          throw error
        })
    },
    [jessicaJonesService, getUtms]
  )

  const toVerify = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event?.preventDefault()
      const email = verify.email
      if (email) {
        setLoadingButton(true)
        await saveUtms()
        if (configs?.hasControlAccess && configs?.controlAccess?.type === 'email' && !configs?.controlAccess?.users?.includes(email)) {
          setValidation({ email: false })
          setError(t('unauthorized'))
          setLoadingButton(false)
          return
        }
        verifyEmail(email?.toLowerCase())
          .then((emailVerify: any) => {
            setError('')
            switch (emailVerify?.status) {
              case 200:
                const toastMessage = {
                  message: t('resendEmail'),
                  closeButton: true,
                  className: 'full text-bold',
                  position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
                  notOverClick: true,
                  immediately: true,
                  duration: 3600000,
                  type: 'success',
                }
                navigate('/login', { state: { toastMessage } })
                break
              case 202:
                navigate(`/login/bind${search}`, { state: { email } })
                break
              default:
                throw new Error('Error verifying email')
            }
          })
          .catch((err) => {
            const error = err as AxiosError
            logService.error({ message: 'Error verifying email', error })
            switch (error?.status) {
              case 400:
                const toastMessage = {
                  message: t('toastMessages.alreadyRegistered'),
                  closeButton: true,
                  className: 'full text-bold',
                  position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
                  notOverClick: true,
                  immediately: true,
                  duration: 3600000,
                }
                navigate('/login', { state: { toastMessage } })
                break
              case 401:
                setValidation({ email: false })
                setError(t('unauthorized'))
                break
              default:
                navigate('/error')
                break
            }
          })
          .finally(() => {
            setLoadingButton(false)
            setDisabled(false)
          })
      }
    },
    [
      navigate,
      search,
      t,
      verify.email,
      verifyEmail,
      logService,
      configs?.controlAccess?.type,
      configs?.controlAccess?.users,
      configs?.hasControlAccess,
      saveUtms,
    ]
  )

  const validate = useCallback((): boolean => {
    return Object.values(validation).every((element) => element)
  }, [validation])

  const loginWithGoogle = useCallback(
    async (network: string, token: string) => {
      try {
        await jessicaJonesService
          ?.verifyEmail('', token, 'google')
          .then((emailVerify: any) => {
            if (emailVerify?.data?.email) {
              setVerify({ email: emailVerify?.data?.email })
              if (
                configs?.hasControlAccess &&
                configs?.controlAccess?.type === 'email' &&
                !configs?.controlAccess?.users?.includes(emailVerify?.data?.email)
              ) {
                setError(t('unauthorized'))
                setLoadingButton(false)
                setDisabled(false)
                navigate('/login')
              }
              setError('')
              switch (emailVerify?.status) {
                case 200:
                  const params = new URLSearchParams(window.location.search)
                  params.set('token', emailVerify?.data?.token)
                  navigate(`/login/register?${params.toString()}`, { state: { provider: network } })
                  break
                case 202:
                  navigate(`/login/bind${search}`, { state: { email: emailVerify?.data?.email, provider: network } })
                  break
                default:
                  throw new Error('Error verifying email')
              }
            }
          })
          .catch((err) => {
            const error = err as AxiosError
            logService.error({ message: 'Error verifying email', error })

            switch (error?.status) {
              case 400:
                navigate(`/login?network=${network}&token=${token}`)
                break
              case 401:
                setValidation({ email: false })
                setError(t('unauthorized'))
                break
              default:
                navigate('/error')
                break
            }
          })
          .finally(() => {
            setLoadingButton(false)
            setDisabled(false)
          })
      } catch (error) {
        logService.error({
          message: 'Error on google login',
          error,
        })
        throw error
      } finally {
        setLoadingButton(false)
      }
    },
    [
      jessicaJonesService,
      logService,
      navigate,
      search,
      t,
      configs?.controlAccess?.type,
      configs?.controlAccess?.users,
      configs?.hasControlAccess,
    ]
  )

  const continueVerifyWithProvider = useCallback(
    async (network: string, token: string) => {
      switch (network) {
        case 'youtube':
          await loginWithGoogle(network, token)
          break
        default:
          break
      }
    },
    [loginWithGoogle]
  )

  const continueVerify = useCallback(async () => {
    const query = new URLSearchParams(window.location.search)
    const token = query.get('token')
    const provider = query.get('network')

    if (token && provider) {
      await continueVerifyWithProvider(provider, token)
    }
  }, [continueVerifyWithProvider])

  const handleEmail = useCallback((email: string) => {
    setVerify({ email })
    setError('')
  }, [])

  useEffect(() => {
    continueVerify()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <h4 className="mb-5">{t('checkMessage')}</h4>
      <form onSubmit={toVerify} style={{ width: '100%' }}>
        <section className="social-login">
          <FirebaseConnect
            providers={['google']}
            onSelectedProvider={(providerSelected: Provider) => doLoginWithProvider(providerSelected)}
            loading={loadingButton}
          />
        </section>
        <Input
          label={t('email')}
          type="email"
          placeholder="email@email.com"
          value={verify.email}
          className="mb-3"
          name="verify-email"
          id="verify-email"
          onChange={(email: string) => handleEmail(email?.trim())}
          onValidate={(email: boolean) => setValidation({ ...validation, email })}
          loading={loadingButton}
          autoComplete="email"
          externalError={error}
          required
        />
        <Button
          type="submit"
          disabled={!validate() || loadingButton || disabled}
          loading={loadingButton}
          className="button-register"
          size="xl"
          id="button-verify"
          style={{ width: '100%' }}
        >
          {t('verify')}
        </Button>
        <p className="mt-5">
          <span>
            <strong>{t('alreadyRegisteredQuestion')}</strong>
          </span>
          <Link to="/login">{t('goHere')}</Link>
        </p>
      </form>
    </div>
  )
}

export default withTransaction('VerifyPageComponent', 'component')(VerifyPageComponent)
